import { useNotifications } from '@armhr/ui';
import {
  Close,
  SearchOutlined,
  SettingsBackupRestore,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApi } from '../../hooks/useApi';
import useFetchHolidays from '../../hooks/useFetchHolidays';

type HolidayRow = {
  id: string;
  defaultName: string;
  replacedHoliday: string | null;
  date: string;
  customName: string | null;
  companyHoliday: CompanyHoliday | null;
};

const GRID_COLUMNS = '150px minmax(0, 1fr) minmax(0, 1.2fr) 44px';

const ManageHolidaysModal: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const api = useApi();
  const { showNotification } = useNotifications();
  const { holidays: calendarHolidays, refresh: refreshCalendarHolidays } =
    useFetchHolidays();

  const [companyHolidays, setCompanyHolidays] = useState<CompanyHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [customizedOnly, setCustomizedOnly] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    String(new Date().getFullYear())
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editNote, setEditNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);

  const rows: HolidayRow[] = useMemo(() => {
    const replacementMap = new Map(
      companyHolidays
        .filter((h) => h.replaced_holiday)
        .map((h) => [h.replaced_holiday!, h])
    );
    const overridesByDate = new Map(
      companyHolidays.map((h) => [h.start_date, h])
    );
    return calendarHolidays
      .map((h) => {
        const defaultName = h.summary || 'Company holiday';
        const override =
          (h.summary ? replacementMap.get(h.summary) : undefined) ??
          overridesByDate.get(h.start_date) ??
          null;
        return {
          id: `${h.start_date}-${defaultName}`,
          defaultName,
          replacedHoliday: h.summary || null,
          date: h.start_date,
          customName: h.override_summary ?? override?.holiday_name ?? null,
          companyHoliday: override ?? null,
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [calendarHolidays, companyHolidays]);

  const availableYears = useMemo(() => {
    const years = new Set(rows.map((row) => row.date.slice(0, 4)));
    return Array.from(years).sort();
  }, [rows]);

  useEffect(() => {
    if (availableYears.length && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const yearRows = useMemo(
    () => rows.filter((row) => row.date.slice(0, 4) === selectedYear),
    [rows, selectedYear]
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return yearRows.filter((row) => {
      if (customizedOnly && !row.customName) return false;
      if (!query) return true;
      return (
        row.defaultName.toLowerCase().includes(query) ||
        (row.customName?.toLowerCase().includes(query) ?? false)
      );
    });
  }, [yearRows, search, customizedOnly]);

  const customizedCount = useMemo(
    () => yearRows.filter((row) => row.customName).length,
    [yearRows]
  );

  const fetchCompanyHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const { results } = await api.company.getCompanyHolidays();
      setCompanyHolidays(results ?? []);
    } catch {
      showNotification({
        message: "Couldn't load holidays",
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) fetchCompanyHolidays();
  }, [open, fetchCompanyHolidays]);

  const startEdit = (row: HolidayRow) => {
    setEditingId(row.id);
    setEditName(row.customName ?? '');
    setEditNote(row.companyHoliday?.description ?? '');
    setShowNote(Boolean(row.companyHoliday?.description));
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError('');
    setShowNote(false);
  };

  const saveEdit = async (row: HolidayRow) => {
    if (!editName.trim()) {
      setEditError('Custom name is required');
      return;
    }
    const payload: CompanyHolidayForm = {
      holiday_name: editName.trim(),
      description: editNote.trim() ? editNote.trim() : null,
      start_date: row.date,
      end_date: row.companyHoliday?.end_date ?? row.date,
      replaced_holiday: row.replacedHoliday,
    };
    try {
      setSaving(true);
      if (row.companyHoliday) {
        await api.company.updateCompanyHoliday(row.companyHoliday.id, payload);
      } else {
        await api.company.createCompanyHoliday(payload);
      }
      showNotification({ message: 'Custom name saved', severity: 'success' });
      cancelEdit();
      fetchCompanyHolidays();
      refreshCalendarHolidays();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showNotification({
        message: detail ?? 'Something went wrong. Please try again.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (id: number) => {
    try {
      await api.company.deleteCompanyHoliday(id);
      showNotification({
        message: 'Reset to default name',
        severity: 'success',
      });
      fetchCompanyHolidays();
      refreshCalendarHolidays();
    } catch {
      showNotification({
        message: "Couldn't reset holiday name",
        severity: 'error',
      });
    }
  };

  const isLoading = loading || calendarHolidays.length === 0;
  const isEmpty = !isLoading && filteredRows.length === 0;

  const renderEditCard = (row: HolidayRow) => (
    <Box
      key={row.id}
      sx={{
        px: 2.5,
        py: 2.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Stack
        direction="row"
        alignItems="baseline"
        spacing={1.25}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {row.defaultName}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {format(parseISO(row.date), 'MMMM d, yyyy')}
        </Typography>
      </Stack>

      <TextField
        autoFocus
        fullWidth
        size="small"
        label="Custom name"
        value={editName}
        placeholder="e.g. Company Winter Break"
        error={Boolean(editError)}
        helperText={
          editError ||
          'Employees see this name on the calendar and time-off pages.'
        }
        onChange={(e) => {
          setEditName(e.target.value);
          if (editError) setEditError('');
        }}
        sx={{ bgcolor: 'white' }}
      />

      {showNote ? (
        <Collapse in={showNote}>
          <TextField
            fullWidth
            size="small"
            value={editNote}
            label="Note (optional)"
            multiline
            minRows={2}
            onChange={(e) => setEditNote(e.target.value)}
            sx={{ mt: 1.5, bgcolor: 'white' }}
          />
        </Collapse>
      ) : (
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          sx={{ mt: 1, display: 'inline-block' }}
          onClick={() => setShowNote(true)}
        >
          Add note
        </Link>
      )}

      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ mt: 2 }}
      >
        <Button variant="text" onClick={cancelEdit} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => saveEdit(row)}
          disabled={saving}
        >
          Save
        </Button>
      </Stack>
    </Box>
  );

  const renderRow = (row: HolidayRow, index: number) => (
    <Box
      key={row.id}
      className="holiday-row"
      onClick={() => startEdit(row)}
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1,
        cursor: 'pointer',
        bgcolor: index % 2 === 1 ? 'grey.50' : 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
        transition: 'background-color 0.15s',
        '&:last-of-type': { borderBottom: 'none' },
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <CompactDate date={row.date} />

      <Typography fontWeight={600} noWrap sx={{ minWidth: 0 }}>
        {row.defaultName}
      </Typography>

      <Box sx={{ minWidth: 0 }}>
        {row.customName ? (
          <Typography color="primary.main" fontWeight={600} noWrap>
            {row.customName}
          </Typography>
        ) : (
          <>
            <Typography
              component="span"
              color="text.disabled"
              sx={{ '.holiday-row:hover &': { display: 'none' } }}
            >
              —
            </Typography>
            <Typography
              component="span"
              variant="body2"
              color="primary.main"
              sx={{
                display: 'none',
                '.holiday-row:hover &': { display: 'inline' },
              }}
            >
              Add custom name
            </Typography>
          </>
        )}
      </Box>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
        {row.companyHoliday && (
          <Tooltip title="Reset to default">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleReset(row.companyHoliday!.id);
              }}
            >
              <SettingsBackupRestore fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Holiday names
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mb: 2, bgcolor: 'white' }}
        >
          Dates come from your payroll calendar and can&apos;t be changed here.
          Give any holiday a custom name and employees will see it on the
          company calendar and time-off pages.
        </Alert>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <TextField
              size="small"
              placeholder="Search holidays"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                flex: { xs: 1, sm: 'none' },
                width: { sm: 220 },
                bgcolor: 'white',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ width: 100, bgcolor: 'white' }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {customizedCount > 0 && (
              <Typography variant="body2" color="text.secondary">
                {customizedCount} customized
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={customizedOnly}
                  onChange={(e) => setCustomizedOnly(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Customized only</Typography>}
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'white',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: GRID_COLUMNS,
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 1,
              bgcolor: 'grey.100',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box />
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Holiday
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Custom name
            </Typography>
            <Box />
          </Box>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {isEmpty && (
            <Typography
              sx={{ py: 6, textAlign: 'center' }}
              color="text.secondary"
            >
              {search || customizedOnly
                ? 'No holidays match your filters'
                : 'No holidays found'}
            </Typography>
          )}

          {!isLoading &&
            filteredRows.map((row, index) =>
              editingId === row.id ? renderEditCard(row) : renderRow(row, index)
            )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const CompactDate: React.FC<{ date: string }> = ({ date }) => {
  const parsed = parseISO(date);
  return (
    <Typography
      component="div"
      sx={{
        fontSize: 14,
        fontWeight: 700,
        color: 'text.primary',
        whiteSpace: 'nowrap',
      }}
    >
      {format(parsed, 'MMMM d')}
      <Box
        component="span"
        sx={{
          ml: 0.75,
          fontSize: 12,
          fontWeight: 400,
          color: 'text.secondary',
        }}
      >
        {format(parsed, 'EEE')}
      </Box>
    </Typography>
  );
};

export default ManageHolidaysModal;
